import {Box, InputAdornment, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AurTextField} from '../../../../common/aur-text-field/AurTextField';
import AurIcon, {
  IconColors,
  IconNames,
} from '../../../../common/aur-icon/AurIcon';
import AurButton from '../../../../common/aur-button/AurButton';
import {ReactComponent as ChevRightIcon} from '../../../../../assests/svg/chev_right_white.svg';
import {
  CreateProjectFormDataType,
  CreateProjectFormFieldType,
} from '../types/createProjectTypes';
import {createProjectFormFields} from '../utils/projectFormConfig';
import HotLeadFormInfoBanner from './HotLeadFormInfoBanner';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import AurServiceAgreementDropdown, {
  ServiceAgreementProps,
} from '../../components/AurServiceAgreementDropdown';
import {storeSelectedAgreement} from '../../../../../redux/slices/projectCreationSlice';
import {IS_HOT_LEAD_FORM_VISIBLE} from '../../../../../constants/constants';
import AurPopup from '../../../../common/aur-popup/AurPopup';
import BillingAgreementDetails from './BillingAgreementDetails';
import {validateProjectName} from '../../../../../api/ClientManagementService';
import {showErrorToast} from '../../../../../api/utils';
import {useToast} from '../../../../../contexts/toastContext/ToastContext';

const stringPath = 'projectCreation.creation.infoForm.';

interface Props {
  onSaveNContinue: () => void;
  projectData: CreateProjectFormDataType;
  onFormDataInput: (data: CreateProjectFormDataType) => void;
  haveUnsavedChanges?: boolean;
  openHotLeadForm?: () => void;
}

const ProjectInfoForm: React.FC<Props> = ({
  onSaveNContinue,
  onFormDataInput,
  projectData,
  haveUnsavedChanges,
  openHotLeadForm,
}) => {
  const dispatch = useDispatch();
  const {showToast} = useToast();
  const {serviceAgreementList} = useSelector(
    (state: RootState) => state.projectCreation
  );

  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const [currentFocusedInput, setCurrentFocusedInput] = useState<string>('');
  const [formData, setFormData] =
    useState<CreateProjectFormDataType>(projectData);

  const [formFieldError, setFormFieldError] = useState<{
    [key in CreateProjectFormFieldType]?: boolean;
  }>({});

  const [isProjectNameAlreadyExists, setIsProjectNameAlreadyExists] =
    useState(false);

  const [showAgreementDetailsPopup, setShowAgreementDetailsPopup] =
    useState(false);

  useEffect(() => {
    onFormDataInput(formData);
  }, [formData, onFormDataInput]);

  const disableSaveButton = useMemo(() => {
    // Project name is required field so it should be not empty
    if ((formData.projectName ?? '').trim() === '') return true;

    // Service agreement is required field so it should be selected
    if (formData.serviceAgreement === '') return true;

    // Checking enterter field have valid value or not
    if (Object.values(formFieldError).some((value) => value)) return true;

    if (isProjectNameAlreadyExists) return true;

    return false;
  }, [formData, formFieldError, isProjectNameAlreadyExists]);

  const selectedAgreementDetail: ServiceAgreementProps | undefined =
    useMemo(() => {
      return serviceAgreementList.find(
        (item) => item.id === formData.serviceAgreement
      );
    }, [serviceAgreementList, formData]);

  /** Validate single field and update result in state */
  const validateField = (
    name: CreateProjectFormFieldType,
    value: string,
    isBlur?: boolean
  ) => {
    if (createProjectFormFields[name]?.validator) {
      const {validator} = createProjectFormFields[name];
      const isValid = validator ? validator(value, isBlur) : true;
      setFormFieldError((prevState) => {
        return {
          ...prevState,
          [name]: !isValid,
        };
      });
    }
  };

  const engagementCode =
    selectedAgreementDetail?.engagementCode;

  const validateProjectNameBeforeContinue = useCallback(async () => {
    const response = await validateProjectName(
      clientId,
      engagementCode ?? '',
      formData.projectName ?? ''
    );
    return response.data.data.isNewProjectNameValid;
  }, [clientId, formData.projectName, engagementCode]);

  const onSaveNContinueClick = useCallback(async () => {
    try {
      const isProjectNameValid = await validateProjectNameBeforeContinue();

      if (!isProjectNameValid) {
        setIsProjectNameAlreadyExists(true);
        return;
      }

      if (selectedAgreementDetail) {
        dispatch(storeSelectedAgreement(selectedAgreementDetail));
      }

      onSaveNContinue();
    } catch (error) {
      showErrorToast(showToast, error, 'center');
    }
  }, [
    validateProjectNameBeforeContinue,
    selectedAgreementDetail,
    dispatch,
    onSaveNContinue,
    showToast,
  ]);

  const handleTextFieldInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target as {
      name: CreateProjectFormFieldType;
      value: string;
    };
    setFormData((prevState) => {
      return {...prevState, [name]: value};
    });
    if (name === 'projectName') {
      setIsProjectNameAlreadyExists(false);
    }
    validateField(name, value, currentFocusedInput !== name);
  };

  const renderTextField = (fieldName: CreateProjectFormFieldType) => {
    const config = createProjectFormFields[fieldName] ?? {};
    const isFocused = currentFocusedInput === fieldName;
    let label, placeholder;
    if (isFocused) {
      label = config.label;
      placeholder = config.placeholder;
    } else {
      if ((formData[fieldName] ?? '') !== '')
        label = config.placeholderWithValue;
      if (!label) label = config.placeholder ?? config.label;
    }

    const haveError =
      formFieldError[fieldName] ||
      (fieldName === 'projectName' && isProjectNameAlreadyExists);

    return (
      <AurTextField
        value={formData[fieldName] ?? ''}
        variant="filled"
        InputProps={{
          disableUnderline: true,
          sx: {
            '& textarea': {
              resize: 'vertical',
              overflow: 'auto',
            },
          },
          endAdornment:
            haveError && config.errorIconVisible ? (
              <InputAdornment position="end">
                <AurIcon
                  icon={IconNames.Error}
                  color={IconColors.Custom}
                  customClassName="input-error-icon"
                />
              </InputAdornment>
            ) : null,
        }}
        label={label ? t(stringPath + label) : undefined}
        placeholder={placeholder ? t(stringPath + placeholder) : undefined}
        inputProps={{
          maxLength: config.maxLength,
        }}
        FormHelperTextProps={{sx: {whiteSpace: 'pre-line'}}}
        helperText={
          fieldName === 'projectName' && isProjectNameAlreadyExists
            ? t(stringPath + 'uniqueProjectNameErrorMsg')
            : t(stringPath + config.helperMsg, {count: config.maxLength})
        }
        className="aur-textfield input-field"
        multiline={config.multiline ?? false}
        minRows={1}
        onFocus={(
          event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          setCurrentFocusedInput(fieldName);
          validateField(fieldName, event.target.value, false);
        }}
        onBlur={(
          event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
        ) => {
          validateField(fieldName, event.target.value, true);
          setCurrentFocusedInput('');
        }}
        name={fieldName}
        onChange={handleTextFieldInput}
        error={haveError}
        data-testid={config.dataTestId}
      />
    );
  };

  const renderSectionTitle = (title: string) => {
    return (
      <Typography className="typo-text subhead3_size section-title-lable">
        {t(stringPath + title)} <span id="optionalBracket">{'('}</span>
        <span>{t(stringPath + 'optional')}</span>
        <span id="optionalBracket">{')'}</span>
      </Typography>
    );
  };

  const renderForm = () => {
    return (
      <div className="input-field-container">
        <Typography className="typo-text headline1_size title-text">
          {t(stringPath + 'title')}
        </Typography>
        <AurServiceAgreementDropdown
          options={serviceAgreementList}
          value={formData.serviceAgreement ?? ''}
          onChange={(value: string) =>
            setFormData((prevState) => {
              return {...prevState, serviceAgreement: value};
            })
          }
          valueLabel={
            formData.serviceAgreement
              ? t(stringPath + 'billingAgreement')
              : undefined
          }
        />
        <AurButton
          type="text"
          label={t(stringPath + 'viewAgreementDetails')}
          classNames="view-agreement-detail-button"
          size="Medium"
          disabled={!selectedAgreementDetail}
          onClick={() => setShowAgreementDetailsPopup(true)}
        />
        {renderTextField('projectName')}
        <span className="section-separator" />
        {/** Further Detail Section */}
        {renderSectionTitle('furtherDetails')}
        {renderTextField('projectDescription')}
        {renderTextField('clientId')}
        {/** Overseeing details */}
        {renderSectionTitle('addOverseeingAttorney')}
        {renderTextField('overseeingName')}
        {renderTextField('emailAddresses')}
        {renderTextField('additionalContactInfo')}
      </div>
    );
  };

  return (
    <Box className="project-info-form-container">
      <div className="top-container">
        {renderForm()}
        {IS_HOT_LEAD_FORM_VISIBLE && (
          <HotLeadFormInfoBanner
            haveUnsavedChange={haveUnsavedChanges}
            openHotLeadForm={openHotLeadForm}
          />
        )}
      </div>

      <div className="footer-button-container">
        <AurButton
          label={t(stringPath + 'saveAndContine')}
          type="continue"
          size="Large"
          dataTestId="saveAndContinue"
          classNames="save-button"
          disabled={disableSaveButton}
          endIcon={<ChevRightIcon />}
          onClick={onSaveNContinueClick}
          keepDisabledFocusable
        />
      </div>

      <AurPopup
        open={showAgreementDetailsPopup}
        showCancelOption={false}
        showCloseIcon={true}
        showPopupTitle
        showPopupHeading
        contentType="element"
        popupHeading={t(stringPath + 'billingAgreementDetails')}
        popupContent={
          <BillingAgreementDetails agreement={selectedAgreementDetail} />
        }
        labelActionOk={t(stringPath + 'popupOkLabel')}
        onOk={() => setShowAgreementDetailsPopup(false)}
        onCancel={() => setShowAgreementDetailsPopup(false)}
        className="aur-unsaved-changes-popup billing-agreement-popup"
      />
    </Box>
  );
};

export default ProjectInfoForm;
